/**
 * Spider-Man — „Marvel: Brains and Brawn".
 *
 * SPIDEY-SENSE: kto útočí na Spider-Mana, ohlási vytlačenú hodnotu útočnej
 * karty ešte pred obranou (`combat.announced`, zapisuje `openCombat`).
 */

import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, drawCard, emit, gainAction, healFighter } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { momentousShift } from './common.js';
import { askMove, emptySpacesInZone, fighterOrNull, opponentOf } from './helpers.js';
import { disarmingShot, snark } from './robinHood.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const SPIDER = 'spider-man';
const POWER = 'spider';

const spiderOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === SPIDER) ?? null;

/** Výsledná hodnota súperovej karty — „if the value of your opponent's card is 4 or higher". */
function theirValue(e: EffectCtx): number | null {
  const combat = e.ctx.state.combat;
  if (!combat?.values) return null;
  const theirs = e.role === 'defender' ? combat.attackCard : combat.defenseCard;
  if (!theirs) return null;
  return e.role === 'defender' ? combat.values.attack : combat.values.defense;
}

/** „Place Spider-Man in any space in his zone." */
function placeInZone(ctx: Ctx, player: PlayerIndex, source: string, label: string): void {
  const spider = spiderOf(ctx, player);
  if (!spider) return;
  const spaces = emptySpacesInZone(ctx, spider.id);
  if (spaces.length === 0) return;
  askChoice(ctx, { kind: 'place', player, source, label, optional: false, fighterId: spider.id, spaces });
}

/** „Recover 3 Health. Place Spider-Man in any space in his zone." */
const friendlyNeighborhood: CardEffect = {
  scheme: (e) => {
    const spider = spiderOf(e.ctx, e.player);
    if (spider) healFighter(e.ctx, spider.id, 3);
    placeInZone(e.ctx, e.player, e.cardId, 'Friendly Neighborhood Spider-Man: polož Spider-Mana v jeho zóne');
  },
};

/** „If the value of your opponent's card is 4 or higher, deal 2 damage to the opposing fighter." */
const counterAttack: CardEffect = {
  after: (e) => {
    if ((theirValue(e) ?? 0) >= 4 && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 2);
  },
};

/** „DURING: Spider-Man cannot take more than 2 combat damage this combat." */
export const cappedAtTwo: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat && e.role === 'defender') (combat.flags ??= []).push('damageCap:2');
  },
};

/** „IMMEDIATELY: Your opponent can't draw cards this turn." */
const rightInTheFace: CardEffect = {
  immediate: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    (e.ctx.state.turn.noDraw ??= []).push(opponent);
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: SPIDER, name: 'Right in the Face: súper tento ťah neťahá karty' });
  },
};

/** „If the value of your opponent's card is 4 or higher, draw 2 cards." */
const spiderSenseTingling: CardEffect = {
  after: (e) => {
    if ((theirValue(e) ?? 0) < 4) return;
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „You may move the opposing fighter a number of spaces up to the amount of combat damage dealt." */
const swingingKick: CardEffect = {
  after: (e) => {
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!them || !e.damage) return;
    askMove(e, { fighters: [them.id], max: e.damage, label: `Swinging Kick: smieš presunúť súpera až o ${e.damage}` });
  },
};

/** „If you won the combat, place Spider-Man in any space in his zone." */
const thwip: CardEffect = {
  after: (e) => {
    if (e.won) placeInZone(e.ctx, e.player, e.cardId, 'Thwip: polož Spider-Mana v jeho zóne');
  },
};

/** „Place Spider-Man in any space in his zone." */
const wallCrawler: CardEffect = {
  after: (e) => placeInZone(e.ctx, e.player, e.cardId, 'Wall-Crawler: polož Spider-Mana v jeho zóne'),
};

/** „All of Spider-Man's attacks this turn are +1 value. Draw 1 card. Gain 1 action." */
const withGreatPower: CardEffect = {
  scheme: (e) => {
    const buffs = (e.ctx.state.turn.buffs ??= {});
    buffs[POWER] = (buffs[POWER] ?? 0) + 1;
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: SPIDER, name: 'With Great Power: útoky Spider-Mana sú tento ťah +1' });
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

export const spiderManCards: EffectRegistry = {
  'spider_man.friendly-neighborhood-spider-man': friendlyNeighborhood,
  'spider_man.counter-attack': counterAttack,
  'spider_man.disarming-shot': disarmingShot,
  'spider_man.momentous-shift': momentousShift,
  'spider_man.web-shooters': cappedAtTwo,
  'spider_man.right-in-the-face': rightInTheFace,
  'spider_man.spider-sense-tingling': spiderSenseTingling,
  'spider_man.swinging-kick': swingingKick,
  'spider_man.thwip': thwip,
  'spider_man.wall-crawler': wallCrawler,
  'spider_man.with-great-power': withGreatPower,
  'spider_man.snark': snark,
};

export const spiderManSpecial: HeroSpecial = {
  name: 'Spidey-Sense',
  hearsAttackValue: true,
  /** With Great Power: +1 k útokom Spider-Mana do konca ťahu. */
  attackModifier: (ctx, player, fighterId) =>
    ctx.state.turn.player === player && fighterOrNull(ctx, fighterId)?.defId === SPIDER ? (ctx.state.turn.buffs?.[POWER] ?? 0) : 0,
};
